import React from "react";
import { GridFilled, GridRegular, PanelRightRegular } from "@fluentui/react-icons";
import { FormattedMessage } from "react-intl";
import {
  PARTICIPANT_LAYOUT_FORCED_STATE,
  normalizeParticipantLayoutForcedState
} from "./participant-layout/participant-layout-room-state";

export { PARTICIPANT_LAYOUT_FORCED_STATE };

export const PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME = "participant-layout-forced-state";
const PARTICIPANT_LAYOUT_ACTIVE_HIDDEN_MENU_ITEM_IDS = new Set([
  "pen",
  "camera",
  "Lobby Cast",
  "tour",
  "addAvatar",
  "upload",
  "assets"
]);

export function getParticipantLayoutManagementCheckedValues({ roomLayoutEnabled = false, forcedState } = {}) {
  const normalizedForcedState = normalizeParticipantLayoutForcedState(forcedState);

  return {
    [PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME]: [
      roomLayoutEnabled ? normalizedForcedState : PARTICIPANT_LAYOUT_FORCED_STATE.CLOSED
    ]
  };
}

export function filterToolbarMoreMenuGroups(groups = []) {
  return groups.filter(group => group && group.items.length > 0);
}

function filterParticipantLayoutActiveMenuItems(items = []) {
  let changed = false;
  const filteredItems = [];

  for (const item of items) {
    if (!item || PARTICIPANT_LAYOUT_ACTIVE_HIDDEN_MENU_ITEM_IDS.has(item.id)) {
      changed = true;
      continue;
    }

    if (!item.submenu) {
      filteredItems.push(item);
      continue;
    }

    const submenu = filterParticipantLayoutActiveMenuItems(item.submenu);

    if (submenu.length === 0 && !item.onClick && !item.href) {
      changed = true;
      continue;
    }

    if (submenu !== item.submenu) {
      changed = true;
      filteredItems.push({
        ...item,
        submenu
      });
      continue;
    }

    filteredItems.push(item);
  }

  return changed ? filteredItems : items;
}

export function filterParticipantLayoutActiveMenuGroups(groups = [], participantLayoutActive = false) {
  if (!participantLayoutActive) {
    return groups;
  }

  let changed = false;
  const filteredGroups = groups.map(group => {
    if (!group) {
      return group;
    }

    const items = filterParticipantLayoutActiveMenuItems(group.items);

    if (items === group.items) {
      return group;
    }

    changed = true;
    return {
      ...group,
      items
    };
  });

  return changed ? filteredGroups : groups;
}

export function createParticipantLayoutMoreMenuItem({
  enabled = false,
  forced = false,
  menuEnabled = false,
  onToggle
} = {}) {
  const unavailable = !menuEnabled || forced;

  if (unavailable) {
    return undefined;
  }

  return {
    id: "participant-layout",
    label: enabled ? (
      <FormattedMessage id="toolbar.participant-layout.show-3d" defaultMessage="Show 3D Layout" />
    ) : (
      <FormattedMessage id="toolbar.participant-layout.show-2d" defaultMessage="Show 2D Layout" />
    ),
    icon: enabled ? <GridFilled /> : <GridRegular />,
    onClick: onToggle,
    disabled: unavailable
  };
}

export function createParticipantLayoutManagementMoreMenuItems({ canManage = false } = {}) {
  if (!canManage) {
    return [];
  }

  return [
    {
      id: "participant-layout-force-none",
      type: "radio",
      name: PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME,
      value: PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
      label: <FormattedMessage id="toolbar.participant-layout.let-users-choose" defaultMessage="Let users choose" />,
      closeMenuOnClick: false
    },
    {
      id: "participant-layout-force-open",
      type: "radio",
      name: PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME,
      value: PARTICIPANT_LAYOUT_FORCED_STATE.OPEN,
      label: <FormattedMessage id="toolbar.participant-layout.force-2d" defaultMessage="Force everyone to 2D" />,
      closeMenuOnClick: false
    },
    {
      id: "participant-layout-force-closed",
      type: "radio",
      name: PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME,
      value: PARTICIPANT_LAYOUT_FORCED_STATE.CLOSED,
      label: <FormattedMessage id="toolbar.participant-layout.force-3d" defaultMessage="Force everyone to 3D" />,
      closeMenuOnClick: false
    }
  ];
}

export function createParticipantLayoutManagementMoreMenuItem({
  canManage = false,
  roomLayoutEnabled = false,
  forcedState = PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
  onManagementCheckedValueChange
} = {}) {
  const managementItems = createParticipantLayoutManagementMoreMenuItems({
    canManage
  });

  if (managementItems.length === 0) {
    return undefined;
  }

  return {
    id: "participant-layout-management",
    label: <FormattedMessage id="toolbar.participant-layout.layout-mode" defaultMessage="Layout Mode" />,
    icon: <PanelRightRegular />,
    submenu: managementItems,
    submenuCheckedValues: getParticipantLayoutManagementCheckedValues({
      roomLayoutEnabled,
      forcedState
    }),
    onSubmenuCheckedValueChange: onManagementCheckedValueChange
  };
}

export function createParticipantLayoutMoreMenuGroup({
  enabled = false,
  forced = false,
  menuEnabled = false,
  onToggle,
  canManage = false,
  roomLayoutEnabled = false,
  forcedState = PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
  onManagementCheckedValueChange
} = {}) {
  const personalToggleItem = createParticipantLayoutMoreMenuItem({
    enabled,
    forced,
    menuEnabled,
    onToggle
  });
  const managementItem = createParticipantLayoutManagementMoreMenuItem({
    canManage,
    roomLayoutEnabled,
    forcedState,
    onManagementCheckedValueChange
  });
  const items = [...(roomLayoutEnabled ? [personalToggleItem] : []), managementItem].filter(Boolean);

  if (items.length === 0) {
    return undefined;
  }

  return {
    title: <FormattedMessage id="toolbar.layout-group" defaultMessage="Layout" />,
    items
  };
}
